import React from 'react';
import { Menu, Button, Loader, Tooltip, Box, Text } from '@mantine/core';
import { IconChevronDown, IconSettings, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTank } from '../contexts/TankContext';

export interface TankSelectorProps {
  /**
   * Text colour for the trigger button.
   *
   * Defaults to teal[7] #116b66 (6.3:1 on the light NoTankSelected surface,
   * where Mantine's subtle-variant teal[6] #178a82 would be only 4.2:1).
   * Callers rendering on a dark surface must pass a light token instead.
   */
  c?: string;
}

export const TankSelector: React.FC<TankSelectorProps> = ({
  c = 'var(--mantine-color-teal-7)',
}) => {
  const { tanks, selectedTank, selectTank, isInitialized, error } = useTank();

  // Branch on isInitialized, NOT isLoading: under React Query v5 a DISABLED
  // query reports isLoading:false, and the tanks query stays disabled while
  // auth is resolving. On a cold load that made this render the empty state,
  // so a user who owns tanks was told to "Create your first tank".
  // isInitialized is true only once the fetch has actually settled.
  if (!isInitialized) {
    return (
      <Button variant="subtle" size="sm" disabled leftSection={<Loader size="xs" />}>
        Loading tanks...
      </Button>
    );
  }

  if (error) {
    return (
      <Tooltip label={error} position="bottom">
        <Button
          component={Link}
          to="/tanks"
          variant="subtle"
          size="sm"
          color="red"
          leftSection={<IconAlertCircle size={14} />}
        >
          Failed to load tanks
        </Button>
      </Tooltip>
    );
  }

  if (tanks.length === 0) {
    return (
      <Button component={Link} to="/tanks" variant="subtle" size="sm" c={c}>
        Create your first tank
      </Button>
    );
  }

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          variant="subtle"
          c={c}
          maw={{ base: 150, sm: 180 }}
          rightSection={<IconChevronDown size={14} />}
          styles={{ label: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
        >
          {selectedTank?.name || 'Select tank'}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Your tanks</Menu.Label>
        {tanks.map((tank) => (
          <Menu.Item
            key={tank.id}
            onClick={() => selectTank(tank.id)}
            styles={{ itemLabel: { minWidth: 0 } }}
            leftSection={
              tank.id === selectedTank?.id ? (
                <IconCheck size={14} data-testid="selected-tank-check" aria-hidden />
              ) : (
                <Box w={14} />
              )
            }
          >
            <Text size="sm" truncate maw={150}>
              {tank.name}
            </Text>
          </Menu.Item>
        ))}

        <Menu.Divider />
        <Menu.Item
          component={Link}
          to="/tanks"
          leftSection={<IconSettings size={14} />}
        >
          Manage tanks
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
